const av = Autodesk.Viewing;
const analytics = av.Private.analytics;
const GlobalManagerMixin = av.GlobalManagerMixin;
const BIMWALK_EXT = "Autodesk.BimWalk";
const LEVEL_EXT   = 'Autodesk.AEC.LevelsExtension';

const DEFAULT_LEVEL_GUID = 'd53d4fd9-1bc6-4549-9084-1caed451e96f'; // Default Revit level guid.

export default class MinimapDropdown {
    constructor(parent) {
        const DROPDOWN_HEIGHT = 50;
        this.parent = parent;
        this.viewer = parent.viewer;
        this.setGlobalManager(this.viewer.globalManager);
        this.dropdownWrapper = null;
        this.parentContainer = parent.mapWidgetHeader;
        this.onClickOptionHandler = this.onClickOption.bind(this);

        this.createWrapper(DROPDOWN_HEIGHT);

        const floorData = this.getFloorDataFromLevelExtension();
        this.createDropdown(floorData);

        this.onLevelChanged = this.onLevelChanged.bind(this);

        this.viewer.addEventListener(Autodesk.AEC.LevelsExtension.LEVEL_CHANGED, this.onLevelChanged);

        this.viewer.getExtensionAsync(LEVEL_EXT).then((levelExt) => {
            levelExt.floorSelector.addEventListener(Autodesk.AEC.FloorSelector.SELECTED_FLOOR_CHANGED, this.onLevelChanged);
            this.onLevelChanged();
        });
    }

    createWrapper(dropdownHeight) {
        const _document = this.getDocument();
        const wrapper = _document.createElement("div");
        wrapper.classList.add("minimap-dropdown-wrapper");
        wrapper.id = 'minimap-dropdown-wrapper';
        wrapper.style.height = dropdownHeight;

        if (!this.parent.expandButton) {
            wrapper.style.paddingLeft = '14px';
        }

        this.parentContainer.appendChild(wrapper);

        this.dropdownWrapper = wrapper;
    }

    createDropdown(floorData) {
        const _document = this.getDocument();
        this.titleElement = _document.createElement("div");
        this.titleElement.classList.add("minimap-title");
        this.dropdownWrapper.appendChild(this.titleElement);
        this.recreateDropdownElement();
        this.createDropdownOptions(floorData);

        this.dropdownWrapper.addEventListener("click", event => {
            this.dropdownElement.classList.toggle("active");
            event.stopPropagation();
        });
    }

    onClickOption(e) {
        const _document = this.getDocument();
        this.titleElement.id = e.target.id;

        const bimWalk = this.viewer.getExtension(BIMWALK_EXT);
        if (bimWalk) {
            bimWalk.jumpToFloor(e.target.id);
        }

        // If synchronizeWithLevelsExt option is enabled
        // AND the levels extension is filtering to a specific floor,
        // selecting a new floor from the minimap dropdown will also set the new floor in the levels extension.
        const levelsExt = this.viewer.getExtension(LEVEL_EXT);
        if (this.parent.options?.synchronizeWithLevelsExt
                && levelsExt
                && levelsExt.floorSelector.currentFloor !== levelsExt.floorSelector.NoFloor) {
            levelsExt.floorSelector.selectFloor(+e.target.id, true);
        }

        const event = _document.createEvent("Event");

        event.initEvent("change", true, true);

        analytics.track('viewer.3d_minimap.levels');

        this.titleElement.dispatchEvent(event);

        Array.from(this.dropdownElement.children).forEach(e =>
            e.classList.remove("active")
        );
        this.dropdownWrapper.classList.toggle("active");
        e.target.classList.toggle("active");
    }

    createDropdownOptions(floorData) {
        // Reordering floors in 3D minimap to match the order in Levels Panel
        for (let i = floorData.length - 1; i >= 0; i--) {
            this.createNewOption(floorData[i].name, i);
        }
    }

    createNewOption(name, id) {
        const _document = this.getDocument();
        const optionElement = _document.createElement("li");

        optionElement.appendChild(_document.createTextNode(name));
        optionElement.setAttribute("id", id);
        optionElement.onclick = this.onClickOptionHandler;
        this.dropdownElement.appendChild(optionElement);
    }

    onLevelChanged() {
        const levelExt = this.viewer.getExtension(LEVEL_EXT);
        const level = levelExt.getCurrentLevel();

        if (level) {
            const floorData = this.getFloorDataFromLevelExtension();
            this.update(level.index, floorData);
        }
    }

    getFloorDataFromLevelExtension() {
        const levelExt = this.viewer.getExtension(LEVEL_EXT);
        if (levelExt?.floorSelector?.floorData) {
            return levelExt.floorSelector.floorData;
        }
        return [];
    }

    recreateDropdownElement() {
        // Recreate dropdownElement in order to get a clean dropdown list.
        const _document = this.getDocument();
        this.dropdownElement && this.dropdownWrapper.removeChild(this.dropdownElement);
        this.dropdownElement = _document.createElement("ul");
        this.dropdownElement.classList.add("minimap-dropdown");
        this.dropdownElement.id = 'minimap-dropdown';
        this.dropdownWrapper.appendChild(this.dropdownElement);
    }

    update(floorIndex, floorData) {
        this.recreateDropdownElement();

        const hasAecModelDataLevels = floorData.some(level => !level.isArtificialLevel);
        const isDefaultRevitLevel = floorData?.length === 1 && floorData[0].guid === DEFAULT_LEVEL_GUID;

        if (hasAecModelDataLevels && !isDefaultRevitLevel) {
            this.dropdownWrapper.classList.remove("no-levels");
            this.createDropdownOptions(floorData);
            if (floorIndex >= 0 && floorData[floorIndex]) {
                const floor = floorData[floorIndex];
                this.dropdownWrapper.style.display = "flex";

                this.setSelectedValue(floor.name);

                if (this.dropdownElement) {
                    Array.from(this.dropdownElement.children).forEach(element => {
                        element.classList.remove("active");
                        if (element.textContent === floor.name) {
                            element.classList.add("active");
                        }
                    });
                }
            }
        } else {
            this.dropdownWrapper.classList.add("no-levels");
            this.setSelectedValue(Autodesk.Viewing.i18n.translate('No Levels'));
        }
    }

    setSelectedValue(valueToSet) {
        this.titleElement.textContent = valueToSet;
    }

    dtor() {
        this.viewer.removeEventListener(Autodesk.AEC.LevelsExtension.LEVEL_CHANGED, this.onLevelChanged);

        const levelExt = this.viewer.getExtension(LEVEL_EXT);
        levelExt?.floorSelector?.removeEventListener(Autodesk.AEC.FloorSelector.SELECTED_FLOOR_CHANGED, this.onLevelChanged);

        this.dropdownWrapper.parentNode.removeChild(this.dropdownWrapper);
        this.dropdownWrapper = null;
    }
}

GlobalManagerMixin.call(MinimapDropdown.prototype);
